import { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';

/**
 * One-time modal that asks a brand-new user for the name they'd like
 * SAGE to use in greetings. Shown on Dashboard mount when `user.name`
 * is empty (email/password signups don't capture a name at register
 * time — Google sign-ins already have it from the OAuth payload).
 *
 * Behaviour:
 *   • Submit → PUT /api/profile { name } → onSaved(name) → close
 *   • Skip → onSkip() → close + persist a localStorage flag so we
 *     never show this modal again for this browser session.
 *
 * The flag is intentionally localStorage-only, not server-side — if
 * the user clears cookies/switches devices SAGE will ask once more,
 * which is strictly better than silently never asking at all.
 */
const DISMISSED_KEY = 'sage_name_prompt_dismissed';

export const hasDismissedNamePrompt = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch (_e) {
    return false;
  }
};

export const markNamePromptDismissed = () => {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch (_e) {
    /* storage disabled — fine, modal just reappears next load */
  }
};

const NamePromptModal = ({ onSaved, onSkip, submitting = false }) => {
  const [name, setName] = useState('');
  const [localSubmitting, setLocalSubmitting] = useState(false);

  const isSubmitting = submitting || localSubmitting;
  const trimmed = name.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmed || isSubmitting) return;
    setLocalSubmitting(true);
    try {
      await onSaved(trimmed);
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="name-prompt-modal"
    >
      <div className="w-full max-w-md bg-stone-950/95 border border-white/10 rounded-sm shadow-2xl p-8">
        <h2
          className="text-2xl font-light text-white mb-2"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          Before we begin
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed mb-6">
          What should SAGE call you? She'll use this once at the top of each
          session — never performatively, never twice in the same breath.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name"
            maxLength={60}
            data-testid="name-prompt-input"
            className="bg-black/40 border-white/10 text-white placeholder:text-zinc-600"
          />
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!trimmed || isSubmitting}
              data-testid="name-prompt-save"
              className="flex-1 frost-button rounded-sm py-2"
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onSkip}
              disabled={isSubmitting}
              data-testid="name-prompt-skip"
              className="text-zinc-400 hover:text-zinc-200"
            >
              Skip for now
            </Button>
          </div>
        </form>

        <p className="text-xs text-zinc-600 mt-4 leading-relaxed">
          You can change this any time in Settings → SAGE Profile.
        </p>
      </div>
    </div>
  );
};

export default NamePromptModal;
